/**
 * ========================================
 * API Service - Backend Communication Layer
 * ========================================
 *
 * Axios 기반 HTTP 클라이언트
 * - JWT 토큰 자동 주입
 * - 에러 핸들링
 * - Request/Response 인터셉터
 */

import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://3.34.129.85/v1';

// Axios instance 생성
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

// Request 인터셉터: JWT 토큰 자동 주입
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response 인터셉터: 에러 핸들링
apiClient.interceptors.response.use(
  (response) => response.data, // 자동으로 data 추출
  (error) => {
    if (error.response?.status === 401) {
      // 인증 실패 시 로그아웃
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_info');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ===========================================
// Admin Auth APIs
// ===========================================

export const adminAuth = {
  /**
   * 관리자 로그인
   * POST /v1/admin/auth/login
   */
  login: async (username, password) => {
    const response = await apiClient.post('/admin/auth/login', {
      username,
      password,
    });

    if (response.success && response.data.token) {
      localStorage.setItem('admin_token', response.data.token);
      localStorage.setItem('admin_info', JSON.stringify(response.data.admin));
    }

    return response;
  },

  /**
   * 로그아웃
   */
  logout: () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_info');
  },

  /**
   * 현재 관리자 정보 조회
   */
  getCurrentAdmin: () => {
    const adminInfo = localStorage.getItem('admin_info');
    return adminInfo ? JSON.parse(adminInfo) : null;
  },

  /**
   * 로그인 여부 확인
   */
  isAuthenticated: () => {
    return !!localStorage.getItem('admin_token');
  },

  /**
   * 비밀번호 변경
   * PUT /v1/admin/auth/change-password
   */
  changePassword: async (currentPassword, newPassword) => {
    return await apiClient.put('/admin/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  },
};

// ===========================================
// Spot Management APIs
// ===========================================

export const spotAPI = {
  /**
   * 스팟 목록 조회
   * GET /v1/admin/spots?page=1&limit=20&region=jeju&strength=strong
   */
  getSpots: async (params = {}) => {
    return await apiClient.get('/admin/spots', { params });
  },

  /**
   * 스팟 생성
   * POST /v1/admin/spots
   */
  createSpot: async (spotData) => {
    return await apiClient.post('/admin/spots', spotData);
  },

  /**
   * 스팟 수정
   * PUT /v1/admin/spots/:id
   */
  updateSpot: async (id, spotData) => {
    return await apiClient.put(`/admin/spots/${id}`, spotData);
  },

  /**
   * 스팟 삭제
   * DELETE /v1/admin/spots/:id
   */
  deleteSpot: async (id) => {
    return await apiClient.delete(`/admin/spots/${id}`);
  },
};

// ===========================================
// Analytics APIs
// ===========================================

export const analyticsAPI = {
  /**
   * 대시보드 통계 조회
   * GET /v1/admin/analytics/dashboard
   */
  getDashboard: async () => {
    return await apiClient.get('/admin/analytics/dashboard');
  },
};

// ===========================================
// Magnetic Field Grade APIs
// ===========================================

export const gradeAPI = {
  /**
   * 등급 판정 기준 조회
   * GET /v1/admin/magnetic-field-grade-rules
   */
  getGradeRules: async () => {
    return await apiClient.get('/admin/magnetic-field-grade-rules');
  },

  /**
   * 등급 판정 기준 생성/수정
   * POST /v1/admin/magnetic-field-grade-rules
   */
  createOrUpdateGradeRule: async (ruleData) => {
    return await apiClient.post('/admin/magnetic-field-grade-rules', ruleData);
  },

  /**
   * 등급 판정 기준 삭제
   * DELETE /v1/admin/magnetic-field-grade-rules/:id
   */
  deleteGradeRule: async (id) => {
    return await apiClient.delete(`/admin/magnetic-field-grade-rules/${id}`);
  },

  /**
   * 표시 설정 조회
   * GET /v1/admin/magnetic-field-display-settings
   */
  getDisplaySettings: async () => {
    return await apiClient.get('/admin/magnetic-field-display-settings');
  },

  /**
   * 표시 설정 업데이트
   * PUT /v1/admin/magnetic-field-display-settings/:key
   */
  updateDisplaySetting: async (key, value) => {
    return await apiClient.put(`/admin/magnetic-field-display-settings/${key}`, { value });
  },
};

// ===========================================
// Zone Classification APIs (추가)
// ===========================================

export const zoneAPI = {
  /**
   * Zone 분류 규칙 조회
   */
  getRules: async () => {
    return await apiClient.get('/admin/zones/rules');
  },

  /**
   * Zone 분류 규칙 생성
   */
  createRule: async (ruleData) => {
    return await apiClient.post('/admin/zones/rules', ruleData);
  },

  /**
   * Zone 분류 규칙 수정
   */
  updateRule: async (id, ruleData) => {
    return await apiClient.put(`/admin/zones/rules/${id}`, ruleData);
  },
};

// ===========================================
// Sensor Data APIs
// ===========================================

export const sensorDataAPI = {
  /**
   * 관리자용 센서 데이터 조회
   * GET /v1/admin/sensor-data?spot_id=1&spot_uuid=xxx&from_date=2024-01-01&to_date=2024-12-31&limit=10000
   */
  getSensorData: async (params = {}) => {
    return await apiClient.get('/admin/sensor-data', { params });
  },
};

export default apiClient;
